'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

interface ToggleProps {
  checked: boolean;
  onChange: (val: boolean) => void;
  id: string;
}

function Toggle({ checked, onChange, id }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      id={id}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ring btn-press ${
        checked ? 'bg-primary' : 'bg-muted'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

type Channel = 'email' | 'inApp' | 'slack';

interface NotifRow {
  id: string;
  label: string;
  description: string;
  email: boolean;
  inApp: boolean;
  slack: boolean;
}

const initialNotifs: NotifRow[] = [
  { id: 'notif-revenue', label: 'Revenue Threshold Alerts', description: 'Notify when daily revenue drops below target by 15%+', email: true, inApp: true, slack: true },
  { id: 'notif-churn', label: 'Churn Rate Spike', description: 'Notify when weekly churn rate increases by 0.5%+', email: true, inApp: true, slack: false },
  { id: 'notif-inventory', label: 'Inventory Critical Alerts', description: 'Notify when any license pool drops below critical threshold', email: true, inApp: true, slack: true },
  { id: 'notif-quota', label: 'Quota Attainment Summary', description: 'Weekly team quota performance digest every Monday', email: true, inApp: false, slack: false },
  { id: 'notif-report', label: 'Scheduled Report Delivery', description: 'Confirmation when scheduled reports are delivered', email: true, inApp: false, slack: false },
  { id: 'notif-ticket', label: 'Support Ticket Escalations', description: 'Notify when tickets exceed 48h without resolution', email: false, inApp: true, slack: true },
  { id: 'notif-login', label: 'New Device Sign-In', description: 'Security alert for sign-in from unrecognized device', email: true, inApp: true, slack: false },
  { id: 'notif-export', label: 'Data Export Completed', description: 'Notify when a large data export finishes processing', email: false, inApp: true, slack: false },
];

export default function NotificationSettings() {
  const [notifs, setNotifs] = useState<NotifRow[]>(initialNotifs);
  const [isSaving, setIsSaving] = useState(false);

  const updateNotif = (id: string, channel: Channel, val: boolean) => {
    setNotifs((prev) =>
      prev.map((n) => (n.id === id ? { ...n, [channel]: val } : n))
    );
  };

  const handleSave = () => {
    setIsSaving(true);
    // Backend integration point: PATCH /api/user/notification-preferences
    setTimeout(() => {
      setIsSaving(false);
      toast.success('Notification preferences saved');
    }, 900);
  };

  return (
    <div className="space-y-5">
      <div className="card-surface p-6">
        <h2 className="text-base font-semibold text-foreground mb-1">Notification Preferences</h2>
        <p className="text-sm text-muted-foreground mb-5">Choose how and where you receive alerts for each event type</p>

        {/* Header */}
        <div className="grid grid-cols-[1fr_80px_80px_80px] gap-4 mb-3 px-1">
          <p className="label-caps text-muted-foreground">Alert Type</p>
          <p className="label-caps text-muted-foreground text-center">Email</p>
          <p className="label-caps text-muted-foreground text-center">In-App</p>
          <p className="label-caps text-muted-foreground text-center">Slack</p>
        </div>

        <div className="space-y-1">
          {notifs.map((notif) => (
            <div
              key={notif.id}
              className="grid grid-cols-[1fr_80px_80px_80px] gap-4 items-center py-3 px-1 rounded-lg hover:bg-muted/40 transition-colors"
            >
              <div>
                <p className="text-sm font-medium text-foreground">{notif.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{notif.description}</p>
              </div>
              <div className="flex justify-center">
                <Toggle
                  id={`${notif.id}-email`}
                  checked={notif.email}
                  onChange={(v) => updateNotif(notif.id, 'email', v)}
                />
              </div>
              <div className="flex justify-center">
                <Toggle
                  id={`${notif.id}-inapp`}
                  checked={notif.inApp}
                  onChange={(v) => updateNotif(notif.id, 'inApp', v)}
                />
              </div>
              <div className="flex justify-center">
                <Toggle
                  id={`${notif.id}-slack`}
                  checked={notif.slack}
                  onChange={(v) => updateNotif(notif.id, 'slack', v)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors btn-press disabled:opacity-70"
        >
          {isSaving ? (
            <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Save size={14} />
          )}
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}